export interface AngebotAgeThreshold {
  days: number;
  label: string;
  className: string;
}

/** Ab diesem Alter gilt ein versendetes Angebot in der Nachfass-Übersicht als überfällig. */
export const ANGEBOT_NACHFASS_MIN_DAYS = 14;

/** Schwellen analog Stundennachweise. Die Übersicht startet bei Kontakten mit Angeboten > 2 Wochen. */
export function getAngebotAgeThresholds(): AngebotAgeThreshold[] {
  return [
    { days: 28, label: "> 4 Wochen", className: "bg-red-600 text-white" },
    { days: 21, label: "> 3 Wochen", className: "bg-orange-500 text-white" },
    { days: 14, label: "> 2 Wochen", className: "bg-amber-500 text-amber-950" },
    { days: 7, label: "> 1 Woche", className: "bg-yellow-400 text-yellow-950" },
  ];
}

export function isAngebotNachfassOverdue(doc: { overdue?: boolean; age_days?: number | null }): boolean {
  if (typeof doc.overdue === "boolean") return doc.overdue;
  return Number(doc.age_days) > ANGEBOT_NACHFASS_MIN_DAYS;
}

export interface AngebotNachfassTouch {
  overdue?: boolean;
  age_days?: number | null;
  nachgefasst_am?: string | null;
  nachgefasst_art?: string | null;
}

/** Angebote, die noch Aktion brauchen, schon nachgefasst sind oder noch jung sind. */
export function partitionNachfassAngebote<T extends AngebotNachfassTouch>(
  angebote: T[],
): { overdue: T[]; followedUp: T[]; newer: T[] } {
  const overdue: T[] = [];
  const followedUp: T[] = [];
  const newer: T[] = [];
  for (const doc of angebote) {
    if (isAngebotNachfassOverdue(doc)) overdue.push(doc);
    else if (doc.nachgefasst_am) followedUp.push(doc);
    else newer.push(doc);
  }
  return { overdue, followedUp, newer };
}

function formatNachfassDate(iso: string): string | null {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Sichtbarer Hinweis, dass das Angebot nochmal rausging oder nachgefragt wurde. */
export function formatAngebotResendLabel(doc: AngebotNachfassTouch): string | null {
  if (!doc.nachgefasst_am) return null;
  const date = formatNachfassDate(doc.nachgefasst_am);
  if (!date) return null;
  const nachfrage = doc.nachgefasst_art === "angebot_nachfass";
  const overdue = isAngebotNachfassOverdue(doc);
  if (nachfrage) {
    return overdue ? `Zuletzt nachgefragt am ${date}` : `Nachgefragt am ${date}`;
  }
  return overdue ? `Zuletzt erneut gesendet am ${date}` : `Erneut gesendet am ${date}`;
}

export function formatAngebotOpenMeta(doc: AngebotNachfassTouch): string | null {
  const resend = formatAngebotResendLabel(doc);
  if (resend && !isAngebotNachfassOverdue(doc)) {
    const days = daysUntilAngebotNachfassDue(Number(doc.age_days) || 0);
    const again =
      days <= 0 ? "wieder fällig" : days === 1 ? "wieder in 1 Tag" : `wieder in ${days} Tagen`;
    return `${resend} · ${again}`;
  }
  if (resend) {
    const since =
      doc.age_days != null ? `${Math.floor(doc.age_days)} Tage seit dem letzten Versand` : null;
    return [resend, since].filter(Boolean).join(" · ");
  }
  if (doc.age_days != null) return `${Math.floor(doc.age_days)} Tage offen`;
  return null;
}

export function daysUntilAngebotNachfassDue(
  ageDays: number,
  minDays = ANGEBOT_NACHFASS_MIN_DAYS,
): number {
  const remaining = minDays - ageDays;
  if (remaining <= 0) return 0;
  return Math.ceil(remaining);
}

/** Tage, bis das älteste Angebot des Kontakts wieder in die Nachfass-Liste fällt. */
export function daysUntilGroupReturns(group: { angebote: Array<{ age_days?: number | null }> }): number {
  if (group.angebote.length === 0) return 0;
  const maxAge = Math.max(...group.angebote.map((angebot) => Number(angebot.age_days) || 0));
  return daysUntilAngebotNachfassDue(maxAge);
}

export function formatDaysUntilNachfassDue(days: number): string {
  if (days <= 0) return "Wieder fällig";
  if (days === 1) return "Wieder in 1 Tag";
  return `Wieder in ${days} Tagen`;
}

export function formatFollowUpCountLabel(
  offers: Array<{ nachgefasst_am?: string | null; nachgefasst_art?: string | null }>,
): string | null {
  const stamped = offers.filter((offer) => offer.nachgefasst_am);
  if (stamped.length === 0) return null;
  const arts = new Set(stamped.map((offer) => offer.nachgefasst_art));
  const noun =
    arts.size === 1 && arts.has("angebot_nachfass")
      ? "nachgefragt"
      : arts.size === 1 && arts.has("erneut")
        ? "erneut gesendet"
        : "nachgefasst";
  return `${stamped.length}× ${noun}`;
}

export function latestNachgefasstOffer<T extends AngebotNachfassTouch>(offers: T[]): T | null {
  let best: T | null = null;
  let bestTs = Number.NEGATIVE_INFINITY;
  for (const offer of offers) {
    if (!offer.nachgefasst_am) continue;
    const ts = Date.parse(offer.nachgefasst_am);
    if (!Number.isFinite(ts) || ts < bestTs) continue;
    best = offer;
    bestTs = ts;
  }
  return best;
}

export function resolveAngebotAgeThreshold(ageDays: number): AngebotAgeThreshold | null {
  const thresholds = getAngebotAgeThresholds();
  return thresholds.find((entry) => ageDays > entry.days) ?? null;
}

export function formatAngebotCurrency(
  amount: number | null | undefined,
  currency = "EUR",
): string {
  if (amount == null || !Number.isFinite(Number(amount))) return "—";
  return Number(amount).toLocaleString("de-DE", {
    style: "currency",
    currency: currency || "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
